import type { GroupType, UserPersona } from '../../generated/users'
import type { UserInfo } from './parseUser'

export enum PermissionsResourceType {
  AnalystStats = 'analyst_stats',
  ApiKey = 'api_key',
  AppFeatures = 'app_features',
  Batch = 'batch',
  DataAdmin = 'data_admin',
  Entity = 'entity',
  Errors = 'errors',
  EstimationsData = 'estimations_data',
  Fact = 'fact',
  Group = 'group',
  IngestionTask = 'ingestion_task',
  KPI = 'kpi',
  Licenses = 'licenses',
  Metric = 'metric',
  MetricsConfig = 'metrics_config',
  Outcomes = 'outcomes',
  PAIsExport = 'pai',
  Portfolio = 'portfolio',
  SDGRevenueAlignment = 'sdg_revenue_alignment',
  SDIRevenue = 'sdi_revenue',
  SDILabels = 'sdi_labels',
  Segments = 'segments',
  Source = 'source',
  SourceTask = 'source_task',
  Technologies = 'technologies',
  Unit = 'unit',
  Users = 'users',
}

export type Qualifier = (string | number)[] | boolean | undefined

export type PermissionResource = {
  [action: string]: Qualifier
}

export type Permissions = Partial<{
  [key in PermissionsResourceType]: PermissionResource
}>

export type GroupMembership = UserInfo['groups'][number]

export type Group = {
  groupId: string
  name: string
  permissions: Permissions | undefined
  type: GroupType
}

type PermissionQuery = [PermissionsResourceType, string, ('?' | string)?]

export type LabelsURIType = {
  service: 'facts' | 'users'
  path: string
}

export type PermissionsResourceSchema = {
  labels_uri: LabelsURIType | null
  actions: string[]
}

export type PermissionsResourceSchemaStrict = PermissionsResourceSchema & {
  labels_uri: LabelsURIType
}

export type PermissionsSchema = Record<string, PermissionsResourceSchema>

export type UserFormType = Pick<
  User,
  'firstName' | 'lastName' | 'email' | 'userPersona' | 'defaultMetricsConfigId'
> & {
  group: string
}

export class User {
  active: boolean

  id: string

  email: string

  firstName: string

  lastName: string

  groups: GroupMembership[]

  userPersona: UserPersona | undefined

  lastLogin: Date | null

  permissions: Permissions | undefined

  canExtract: boolean

  canAccessErrors: boolean

  canAccessFacts: boolean

  canAccessMetrics: boolean

  canAccessUnits: boolean

  canAccessEntities: boolean

  canAccessSources: boolean

  canAccessSDGRevenueAlignment: boolean

  canAccessSegments: boolean

  canAccessTechnologies: boolean

  canAccessEstimations: boolean

  canAccessLookup: boolean

  canAccessUsers: boolean

  canAccessCreate: boolean

  canAccessGroups: boolean

  canAccessKpis: boolean

  defaultMetricsConfigId: string

  constructor(data: UserInfo) {
    this.active = data.active
    this.id = data.userId
    this.email = data.email
    this.firstName = data.firstName
    this.lastName = data.lastName
    this.lastLogin = data.lastLogin
    this.permissions = data?.permissions
    this.groups = data.groups

    this.userPersona = data.userPersona

    this.canExtract = this.hasAnyPerm([
      [PermissionsResourceType.IngestionTask, 'GET', '?'],
      [PermissionsResourceType.IngestionTask, 'EXTRACT', '?'],
      [PermissionsResourceType.IngestionTask, 'VERIFY', '?'],
      [PermissionsResourceType.IngestionTask, 'QA', '?'],
      [PermissionsResourceType.SourceTask, 'GET', '?'],
      [PermissionsResourceType.SourceTask, 'EXTRACT', '?'],
      [PermissionsResourceType.SourceTask, 'VERIFY', '?'],
      [PermissionsResourceType.SourceTask, 'QA', '?'],
    ])
    this.canAccessErrors = this.hasPerm(PermissionsResourceType.Errors, 'LIST')
    this.canAccessFacts = this.hasAllPerms([
      [PermissionsResourceType.Fact, 'LIST'],
      [PermissionsResourceType.Fact, 'GET'],
    ])
    this.canAccessMetrics = this.hasPerm(PermissionsResourceType.Metric, 'LIST')
    this.canAccessUnits = this.hasPerm(PermissionsResourceType.Unit, 'LIST')
    this.canAccessEntities = this.hasPerm(PermissionsResourceType.Entity, 'LIST')
    this.canAccessSources = this.hasPerm(PermissionsResourceType.Source, 'LIST')
    this.canAccessSegments = this.hasPerm(PermissionsResourceType.Segments, 'LIST')
    this.canAccessTechnologies = this.hasPerm(PermissionsResourceType.Technologies, 'LIST')
    this.canAccessEstimations = this.hasPerm(PermissionsResourceType.EstimationsData, 'LIST')
    this.canAccessSDGRevenueAlignment = this.hasPerm(
      PermissionsResourceType.SDGRevenueAlignment,
      'UPLOAD',
    )
    this.canAccessLookup = this.hasPerm(PermissionsResourceType.Entity, 'LOOKUP')
    this.canAccessCreate = this.hasAnyPerm([
      [PermissionsResourceType.Entity, 'CREATE'],
      [PermissionsResourceType.IngestionTask, 'CREATE'],
      [PermissionsResourceType.SourceTask, 'CREATE'],
      [PermissionsResourceType.Fact, 'CREATE'],
    ])
    this.canAccessUsers = this.hasPerm(PermissionsResourceType.Users, 'LIST', '?')
    this.canAccessGroups = this.hasPerm(PermissionsResourceType.Group, 'LIST')
    this.canAccessKpis = this.hasPerm(PermissionsResourceType.KPI, 'LIST')
    this.defaultMetricsConfigId = data.defaultMetricsConfigId
  }

  /**
   * Checks if a given permission set (e.g. user permissions or group permissions) has a resource enabled.
   * Returns undefined if a resource is not specified.
   */
  private static permissionSetHasPerm(
    permissions: Permissions | undefined,
    resource: PermissionsResourceType,
    action: string,
    label?: '?' | string | number,
  ): boolean | undefined {
    const qualifier = permissions?.[resource]?.[action]
    if (qualifier === undefined) {
      return undefined
    }
    if (typeof qualifier === 'boolean') {
      return qualifier
    }
    // from this point we know qualifier is an array
    if (label === undefined) {
      return false
    }
    if (label === '?') {
      return qualifier.length > 0
    }
    return qualifier.includes(label)
  }

  /**
   *  Determines if user has permission, optionally qualified by a label.
   *  No label means unqualified permissions .
   *  Label "?" means wildcard (any label).
   */
  hasPerm(
    resource: PermissionsResourceType,
    action: string,
    label?: '?' | string | number,
  ): boolean {
    const ownPermission = User.permissionSetHasPerm(this.permissions, resource, action, label)
    if (ownPermission !== undefined) {
      return ownPermission
    }
    for (const membership of this.groups) {
      const groupPermission = User.permissionSetHasPerm(
        membership.permissions,
        resource,
        action,
        label,
      )
      if (groupPermission !== undefined) {
        return groupPermission
      }
    }
    return false
  }

  hasAnyPerm(permissions: PermissionQuery[]): boolean {
    return permissions.some((args) => this.hasPerm(...args))
  }

  hasAllPerms(permissions: PermissionQuery[]): boolean {
    return permissions.every((args) => this.hasPerm(...args))
  }

  getLabels(resource: PermissionsResourceType, action: string): (string | number)[] | undefined {
    const qualifier = this.permissions?.[resource]?.[action]
    if (Array.isArray(qualifier)) {
      return qualifier
    }
    return
  }

  getGroupIds(): string[] {
    return this.groups.map((group) => group.groupId)
  }

  getGroupMembership(groupId: string): GroupMembership | undefined {
    return this.groups.find((group) => group.groupId === groupId)
  }

  isMemberOfGroup(groupId: string): boolean {
    const membership = this.getGroupMembership(groupId)
    return Boolean(membership)
  }
}
